import {crc32OfBlob} from './crc32';
import {fitWithin} from './fit';

// Browser-side resize (Concepts §9): the 1600px JPEG the app keeps plus a 400px thumbnail,
// orientation baked in. Starts the moment a photo is chosen. Fails open: an image the browser
// can decode but not re-encode is sent as it is; one it cannot decode at all (HEIC from a
// library pick on some iOS setups) is refused with a message.

export const MAIN_MAX = 1600;
export const THUMB_MAX = 400;
export const MAIN_QUALITY = 0.82;
export const THUMB_QUALITY = 0.8;
const MAX_ORIGINAL_BYTES = 6 * 1024 * 1024;

export interface PreparedPhoto {
    main: Blob;
    thumb: Blob | null;
    width: number;
    height: number;
    crc32: number;
    contentType: string;
}

export class UnreadablePhotoError extends Error {
    constructor() {
        super("That photo can't be read here (HEIC, perhaps). Try taking it with the camera, or pick a JPEG.");
        this.name = 'UnreadablePhotoError';
    }
}

type Decoded = {source: CanvasImageSource; width: number; height: number; close(): void};

async function decode(file: File): Promise<Decoded> {
    if ('createImageBitmap' in window) {
        try {
            const bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'});
            return {source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close()};
        } catch {
            // fall through to <img>
        }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new UnreadablePhotoError());
            el.src = url;
        });
        return {source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url)};
    } catch (error) {
        URL.revokeObjectURL(url);
        throw error;
    }
}

async function encode(decoded: Decoded, max: number, quality: number): Promise<{blob: Blob; width: number; height: number}> {
    const {width, height} = fitWithin(decoded.width, decoded.height, max);
    const canvas = 'OffscreenCanvas' in window ? new OffscreenCanvas(width, height) : Object.assign(document.createElement('canvas'), {width, height});
    const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('no canvas');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(decoded.source, 0, 0, width, height);
    const blob =
        canvas instanceof OffscreenCanvas
            ? await canvas.convertToBlob({type: 'image/jpeg', quality})
            : await new Promise<Blob>((resolve, reject) => (canvas as HTMLCanvasElement).toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', quality));
    return {blob, width, height};
}

const isSendableAsIs = (file: File): boolean => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type) && file.size <= MAX_ORIGINAL_BYTES;

export async function prepareUpload(file: File): Promise<PreparedPhoto> {
    let decoded: Decoded;
    try {
        decoded = await decode(file);
    } catch (error) {
        if (isSendableAsIs(file)) {
            // Can't decode it here, but the server can store it and other devices may show it.
            return {main: file, thumb: null, width: 0, height: 0, crc32: await crc32OfBlob(file), contentType: file.type};
        }
        throw error instanceof UnreadablePhotoError ? error : new UnreadablePhotoError();
    }

    try {
        const main = await encode(decoded, MAIN_MAX, MAIN_QUALITY);
        let thumb: Blob | null = null;
        try {
            thumb = (await encode(decoded, THUMB_MAX, THUMB_QUALITY)).blob;
        } catch {
            thumb = null; // the card falls back to the main image
        }
        return {main: main.blob, thumb, width: main.width, height: main.height, crc32: await crc32OfBlob(main.blob), contentType: 'image/jpeg'};
    } catch {
        if (isSendableAsIs(file)) {
            return {main: file, thumb: null, width: decoded.width, height: decoded.height, crc32: await crc32OfBlob(file), contentType: file.type};
        }
        throw new UnreadablePhotoError();
    } finally {
        decoded.close();
    }
}
